import { useState, useRef, useCallback, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { GripVertical, Check, ChevronDown, ChevronUp, Loader2, Search } from "lucide-react";
import {
  getSearchProviderConfig,
  updateSearchProviderConfig,
  type SearchProvider,
  type SearchProviderRoutingMode,
} from "../../services/AppConfigService";
import ProductSearchRadiusSettings from "./ProductSearchRadiusSettings";

const PROVIDER_META: Record<string, { color: string; icon: string }> = {
  walmartapp: { color: "blue", icon: "🛒" },
  tempo: { color: "purple", icon: "🔍" },
  zephyr: { color: "emerald", icon: "⚡" },
};

const colorClasses: Record<string, { bg: string; border: string; badge: string; dot: string }> = {
  blue: {
    bg: "bg-blue-50 dark:bg-blue-950/30",
    border: "border-blue-200 dark:border-blue-800",
    badge: "bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300",
    dot: "bg-blue-500",
  },
  purple: {
    bg: "bg-purple-50 dark:bg-purple-950/30",
    border: "border-purple-200 dark:border-purple-800",
    badge: "bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300",
    dot: "bg-purple-500",
  },
  emerald: {
    bg: "bg-emerald-50 dark:bg-emerald-950/30",
    border: "border-emerald-200 dark:border-emerald-800",
    badge: "bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300",
    dot: "bg-emerald-500",
  },
};

export default function ProductSearchTab() {
  const queryClient = useQueryClient();
  const [providers, setProviders] = useState<SearchProvider[]>([]);
  const [routingMode, setRoutingMode] = useState<SearchProviderRoutingMode>("fallback");
  const [dirty, setDirty] = useState(false);
  const [saved, setSaved] = useState(false);
  const dragItem = useRef<number | null>(null);
  const dragOverItem = useRef<number | null>(null);
  const [draggingIdx, setDraggingIdx] = useState<number | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["search-provider-routing"],
    queryFn: getSearchProviderConfig,
  });

  // Sync from the server only while the local list is pristine — a background
  // refetch (window refocus, tab remount) must not wipe an unsaved reorder.
  const dirtyRef = useRef(dirty);
  dirtyRef.current = dirty;
  useEffect(() => {
    if (data && data.providers.length > 0 && !dirtyRef.current) {
      setProviders(data.providers);
      setRoutingMode(data.routingMode);
    }
  }, [data]);

  const mutation = useMutation({
    mutationFn: updateSearchProviderConfig,
    onSuccess: (data) => {
      queryClient.setQueryData(["search-provider-routing"], data);
      queryClient.setQueryData(["search-providers"], data.providers);
      setProviders(data.providers);
      setRoutingMode(data.routingMode);
      setDirty(false);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    },
  });

  const handleToggle = useCallback((index: number) => {
    setProviders((prev) => {
      const next = prev.map((provider) => ({ ...provider }));
      const enabled = !next[index].enabled;
      next[index].enabled = enabled;
      if (!enabled) next[index].trafficPercent = 0;

      const enabledProviders = next.filter((provider) => provider.enabled);
      if (enabledProviders.length === 1) {
        enabledProviders[0].trafficPercent = 100;
      }
      return next;
    });
    setDirty(true);
  }, []);

  const handleTrafficPercent = useCallback((index: number, value: string) => {
    const parsed = Number(value);
    const trafficPercent = Number.isFinite(parsed) ? Math.min(100, Math.max(0, Math.round(parsed))) : 0;
    setProviders((prev) => prev.map((provider, providerIndex) => (
      providerIndex === index ? { ...provider, trafficPercent } : provider
    )));
    setDirty(true);
  }, []);

  const handleRoutingMode = useCallback((mode: SearchProviderRoutingMode) => {
    setRoutingMode(mode);
    setDirty(true);
  }, []);

  const handleDragStart = (index: number, e: React.DragEvent) => {
    // Firefox refuses to start a drag unless dragstart sets data; Chrome is
    // merely inconsistent without it. This line is what makes dragging work.
    e.dataTransfer.setData("text/plain", String(index));
    e.dataTransfer.effectAllowed = "move";
    dragItem.current = index;
    dragOverItem.current = null;
    setDraggingIdx(index);
  };

  /** Arrow-button fallback: always works, no drag gesture required. */
  const moveProvider = useCallback((index: number, delta: -1 | 1) => {
    setProviders((prev) => {
      const target = index + delta;
      if (target < 0 || target >= prev.length) return prev;
      const next = [...prev];
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
    setDirty(true);
  }, []);

  const handleDragEnter = (index: number) => {
    dragOverItem.current = index;
  };

  const handleDragEnd = () => {
    if (dragItem.current === null || dragOverItem.current === null || dragItem.current === dragOverItem.current) {
      setDraggingIdx(null);
      return;
    }
    setProviders((prev) => {
      const next = [...prev];
      const [moved] = next.splice(dragItem.current!, 1);
      next.splice(dragOverItem.current!, 0, moved);
      return next;
    });
    dragItem.current = null;
    dragOverItem.current = null;
    setDraggingIdx(null);
    setDirty(true);
  };

  // Touch drag support
  const touchStartY = useRef(0);
  const touchIdx = useRef<number | null>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const handleTouchStart = (index: number, e: React.TouchEvent) => {
    touchIdx.current = index;
    touchStartY.current = e.touches[0].clientY;
    setDraggingIdx(index);
  };

  const handleTouchMove = useCallback((e: TouchEvent) => {
    if (touchIdx.current === null || !listRef.current) return;
    e.preventDefault();
    const y = e.touches[0].clientY;
    const items = listRef.current.querySelectorAll("[data-provider-item]");
    for (let i = 0; i < items.length; i++) {
      const rect = items[i].getBoundingClientRect();
      if (y >= rect.top && y <= rect.bottom && i !== touchIdx.current) {
        dragOverItem.current = i;
        break;
      }
    }
  }, []);

  const handleTouchEnd = useCallback(() => {
    if (touchIdx.current === null || dragOverItem.current === null || touchIdx.current === dragOverItem.current) {
      touchIdx.current = null;
      setDraggingIdx(null);
      return;
    }
    setProviders((prev) => {
      const next = [...prev];
      const [moved] = next.splice(touchIdx.current!, 1);
      next.splice(dragOverItem.current!, 0, moved);
      return next;
    });
    touchIdx.current = null;
    dragOverItem.current = null;
    setDraggingIdx(null);
    setDirty(true);
  }, []);

  useEffect(() => {
    const el = listRef.current;
    if (!el) return;
    el.addEventListener("touchmove", handleTouchMove, { passive: false });
    el.addEventListener("touchend", handleTouchEnd);
    return () => {
      el.removeEventListener("touchmove", handleTouchMove);
      el.removeEventListener("touchend", handleTouchEnd);
    };
  }, [handleTouchMove, handleTouchEnd]);

  const handleSave = () => mutation.mutate({ routingMode, providers });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
      </div>
    );
  }

  const enabledCount = providers.filter((p) => p.enabled).length;
  const enabledTrafficTotal = providers.reduce(
    (total, provider) => total + (provider.enabled ? provider.trafficPercent : 0),
    0,
  );
  const trafficIsValid = routingMode === "fallback" || enabledCount === 0 || enabledTrafficTotal === 100;

  return (
    <div className="max-w-2xl">
      <div className="flex items-center gap-2 mb-1">
        <Search className="w-4 h-4 text-gray-500 dark:text-dark-400" />
        <h2 className="text-sm font-semibold text-gray-900 dark:text-white">Product Search Providers</h2>
      </div>
      <p className="text-xs text-gray-500 dark:text-dark-400 mb-4">
        Choose whether searches always start at the top or distribute their first attempt by percentage. Both modes fall through in priority order when a provider fails or has no price.
      </p>

      <div className="mb-4 grid grid-cols-1 gap-2 sm:grid-cols-2" aria-label="Search provider routing mode">
        <button
          type="button"
          aria-pressed={routingMode === "fallback"}
          onClick={() => handleRoutingMode("fallback")}
          className={`rounded-lg border px-3 py-2.5 text-left transition-colors ${
            routingMode === "fallback"
              ? "border-blue-500 bg-blue-50 text-blue-900 dark:border-blue-500 dark:bg-blue-950/40 dark:text-blue-100"
              : "border-gray-200 bg-white text-gray-700 dark:border-dark-700 dark:bg-dark-800 dark:text-dark-200"
          }`}
        >
          <span className="block text-xs font-semibold">Ordered fallback</span>
          <span className="mt-0.5 block text-[11px] opacity-75">Everyone starts with provider #1.</span>
        </button>
        <button
          type="button"
          aria-pressed={routingMode === "percentage"}
          onClick={() => handleRoutingMode("percentage")}
          className={`rounded-lg border px-3 py-2.5 text-left transition-colors ${
            routingMode === "percentage"
              ? "border-purple-500 bg-purple-50 text-purple-900 dark:border-purple-500 dark:bg-purple-950/40 dark:text-purple-100"
              : "border-gray-200 bg-white text-gray-700 dark:border-dark-700 dark:bg-dark-800 dark:text-dark-200"
          }`}
        >
          <span className="block text-xs font-semibold">Percentage split</span>
          <span className="mt-0.5 block text-[11px] opacity-75">Traffic chooses the first provider.</span>
        </button>
      </div>

      <div ref={listRef} className="space-y-2">
        {providers.map((provider, index) => {
          const meta = PROVIDER_META[provider.type] || { color: "blue", icon: "🔌" };
          const cls = colorClasses[meta.color] || colorClasses.blue;
          const isDragging = draggingIdx === index;

          return (
            <div
              key={provider.type}
              data-provider-item
              draggable
              onDragStart={(e) => handleDragStart(index, e)}
              onDragEnter={() => handleDragEnter(index)}
              onDragEnd={handleDragEnd}
              onDragOver={(e) => e.preventDefault()}
              onTouchStart={(e) => handleTouchStart(index, e)}
              className={`flex items-center gap-3 rounded-lg border px-3 py-2.5 transition-all select-none ${
                isDragging ? "opacity-50 scale-[0.98]" : ""
              } ${provider.enabled ? `${cls.bg} ${cls.border}` : "bg-gray-50 dark:bg-dark-900 border-gray-200 dark:border-dark-700 opacity-60"}`}
            >
              <div className="cursor-grab active:cursor-grabbing touch-none text-gray-400 dark:text-dark-500">
                <GripVertical className="w-4 h-4" />
              </div>

              <span className="text-base leading-none">{meta.icon}</span>

              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold text-gray-900 dark:text-white">{provider.label}</span>
                  <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded ${cls.badge}`}>{provider.type}</span>
                </div>
              </div>

              <label className="flex items-center gap-1 text-xs font-semibold text-gray-600 dark:text-dark-300">
                <span className="sr-only">{provider.label} traffic percentage</span>
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="1"
                  aria-label={`${provider.label} traffic percentage`}
                  value={provider.trafficPercent}
                  disabled={!provider.enabled || routingMode !== "percentage"}
                  onPointerDown={(e) => e.stopPropagation()}
                  onTouchStart={(e) => e.stopPropagation()}
                  onChange={(e) => handleTrafficPercent(index, e.target.value)}
                  className="w-16 rounded-md border border-gray-200 bg-white px-2 py-1 text-right font-mono text-xs text-gray-900 disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-400 dark:border-dark-600 dark:bg-dark-800 dark:text-white dark:disabled:bg-dark-900"
                />
                <span>%</span>
              </label>

              <span className="text-[10px] font-bold text-gray-400 dark:text-dark-500 tabular-nums w-4 text-center">
                {index + 1}
              </span>

              <div className="flex flex-col">
                <button
                  type="button"
                  aria-label={`Move ${provider.label} up`}
                  onClick={() => moveProvider(index, -1)}
                  disabled={index === 0}
                  className="text-gray-400 dark:text-dark-500 hover:text-gray-700 dark:hover:text-dark-200 disabled:opacity-25 disabled:cursor-default"
                >
                  <ChevronUp className="w-3.5 h-3.5" />
                </button>
                <button
                  type="button"
                  aria-label={`Move ${provider.label} down`}
                  onClick={() => moveProvider(index, 1)}
                  disabled={index === providers.length - 1}
                  className="text-gray-400 dark:text-dark-500 hover:text-gray-700 dark:hover:text-dark-200 disabled:opacity-25 disabled:cursor-default"
                >
                  <ChevronDown className="w-3.5 h-3.5" />
                </button>
              </div>

              <button
                onPointerDown={() => handleToggle(index)}
                className={`relative w-9 h-5 rounded-full transition-colors ${
                  provider.enabled ? "bg-emerald-500" : "bg-gray-300 dark:bg-dark-600"
                }`}
              >
                <span
                  className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${
                    provider.enabled ? "translate-x-4" : "translate-x-0"
                  }`}
                />
              </button>
            </div>
          );
        })}
      </div>

      {enabledCount === 0 && (
        <p className="mt-2 text-xs text-amber-600 dark:text-amber-400 font-medium">
          ⚠ No providers enabled — product search will fail.
        </p>
      )}

      {routingMode === "percentage" && enabledCount > 0 && (
        <p className={`mt-2 text-xs font-medium ${
          trafficIsValid
            ? "text-emerald-600 dark:text-emerald-400"
            : "text-amber-600 dark:text-amber-400"
        }`}>
          Primary traffic allocation: {enabledTrafficTotal}% / 100%
          {!trafficIsValid && " — enabled providers must total 100% before saving."}
        </p>
      )}

      {routingMode === "fallback" && enabledCount > 0 && (
        <p className="mt-2 text-xs font-medium text-blue-600 dark:text-blue-400">
          Every uncached search starts with the first enabled provider; percentages are ignored.
        </p>
      )}

      <div className="mt-4 flex items-center gap-3">
        <button
          onPointerDown={handleSave}
          disabled={!dirty || !trafficIsValid || mutation.isPending}
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold text-white bg-red-600 hover:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        >
          {mutation.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : saved ? <Check className="w-3.5 h-3.5" /> : null}
          {saved ? "Saved" : "Save Changes"}
        </button>
        {mutation.isError && (
          <span className="text-xs text-red-600 dark:text-red-400">Failed to save</span>
        )}
      </div>

      <ProductSearchRadiusSettings />
    </div>
  );
}
